import React, { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';

import {
  DatePicker,
  FieldWrapperVertical,
  SnackbarApiErrorMessage,
  TextArea,
  useDataProvider,
  useDialog,
  useLocalization,
  useMasterData,
  useSnackbar,
} from '@ui-framework/index';
import DialogCloseButton from '@product-framework/components/Dialogs/DialogCloseButton';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, CircularProgress, Typography } from '@mui/material';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { UserRoleSelect } from '@product-framework/components/FormFields';
import { IconLibrary } from '@product-framework/index';
import dayjs from 'dayjs';

export default function GenerateApiKeyDialog({
  open,
  onClose,
  serviceProviderId,
  reRenderList = () => undefined,
}) {
  const { confirmationRef } = useDialog();
  const { formatMessage } = useLocalization();
  const [loading, setLoading] = useState(false);
  const snackbar = useSnackbar();
  const { defaultApiKeyExpiration = { unit: 'year', value: 2 } } =
    useMasterData();

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { getApiUrl, post } = useDataProvider(provider);

  const schema = yup.object().shape({
    description: yup.string().required(
      formatMessage({
        id: 'DESCRIPTION_IS_REQUIRED',
        defaultMessage: 'Description is required',
      }),
    ),
    userRole: yup.object().required(
      formatMessage({
        id: 'USER_ROLE_IS_REQUIRED',
        defaultMessage: 'User Role is required',
      }),
    ),
    expiresAt: yup
      .date()
      .typeError(
        formatMessage({
          id: 'EXPIRES_AT_INVALID_DATE',
          defaultMessage: 'Please enter a valid date',
        }),
      )
      .nullable()
      .min(
        dayjs().startOf('day').toDate(),
        formatMessage({
          id: 'EXPIRES_AT_MUST_BE_FUTURE_DATE',
          defaultMessage: 'Expiration Date must be a future date',
        }),
      )
      .max(
        dayjs()
          .add(defaultApiKeyExpiration.value, defaultApiKeyExpiration.unit)
          .toDate(),
        formatMessage({
          id: 'EXPIRES_AT_MUST_BE_LESS_THAN_2_YEARS',
          defaultMessage: `Expiration Date must be less than ${defaultApiKeyExpiration.value} ${defaultApiKeyExpiration.unit}${defaultApiKeyExpiration.value > 1 ? 's' : ''} from today`,
        }),
      ),
  });
  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      expiresAt: dayjs()
        .add(defaultApiKeyExpiration.value, defaultApiKeyExpiration.unit)
        .format('YYYY-MM-DD'),
    },
  });

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = methods;

  const preProcessRequestData = data => {
    const processedData = {
      description: data.description,
      userRoleId: data.userRole?.value,
      expiresAt: data.expiresAt || undefined,
    };
    return processedData;
  };

  async function onFormSubmit(data) {
    setLoading(true);
    const body = preProcessRequestData(data);
    const response = await post(
      `${getApiUrl()}/sxcl/v1/service-providers/${serviceProviderId}/api-key`,
      body,
    );
    if (response.ok) {
      confirmationRef.current.init({
        message: (
          <Box>
            <Typography variant="body2">
              Please copy the below key, you will not be able to access it once
              you close this popup
            </Typography>

            <Box sx={{ mt: 2 }} display="flex" alignItems="center" gap={1}>
              <Typography sx={{ fontWeight: 'bold' }}>
                {response?.data?.key}
              </Typography>
              <Button
                variant="text"
                size="small"
                onClick={() => {
                  navigator.clipboard.writeText(response?.data?.key);
                  snackbar.showMessage({
                    type: 'success',
                    data: 'API key copied to clipboard!',
                  });
                }}>
                <IconLibrary.Copy />
              </Button>
            </Box>
          </Box>
        ),
        positiveButtonText: 'Close',
        width: 360,
        title: 'Api Key Generated',
        onChange: () => {
          reRenderList();
          confirmationRef.current.setOpen(false);
          onClose();
        },
      });
      confirmationRef.current.setOpen(true);
    } else {
      snackbar.showMessage({
        type: 'error',
        data: <SnackbarApiErrorMessage errors={response?.errors} />,
      });
    }
    methods.reset();
    setLoading(false);
  }

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {formatMessage({
          id: 'ADD_API_KEY_HEADER',
          defaultMessage: 'API Key',
        })}
        <DialogCloseButton disabled={loading} onClose={onClose} />
      </DialogTitle>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <DialogContent>
            {loading ? (
              <Box textAlign="center">
                <CircularProgress size="30px" />
                <Typography>Processing</Typography>
              </Box>
            ) : (
              <Grid2 container spacing={2} direction="column">
                <Grid2 xs={12}>
                  <TextArea
                    label={formatMessage({
                      id: 'DESCRIPTION_LABEL',
                      defaultMessage: 'Description',
                    })}
                    name="description"
                    rows={1}
                    {...methods.register('description')}
                    error={!!errors?.description?.message}
                    helperText={errors?.description?.message || ''}
                  />
                </Grid2>
                <Grid2 xs={12}>
                  <Controller
                    name="userRole"
                    control={control}
                    render={({ field, fieldState }) => (
                      <FieldWrapperVertical
                        field={{
                          name: 'UserRole',
                          companyType: 'SERVICE_PROVIDER',
                          label: {
                            localeId: 'USER_ROLE_LABEL',
                            defaultMessage: 'User Role',
                          },
                        }}>
                        <UserRoleSelect
                          control={control}
                          {...field}
                          field={{
                            name: 'userRole',
                            companyType: 'SERVICE_PROVIDER',
                          }}
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                          isClearable
                          inPopUp
                        />
                      </FieldWrapperVertical>
                    )}
                  />
                </Grid2>
                <Grid2 xs={12}>
                  <Controller
                    name="expiresAt"
                    control={control}
                    render={({ field }) => (
                      <FieldWrapperVertical
                        field={{
                          name: 'expiresAt',
                          label: {
                            localeId: 'EXPIRES_AT_LABEL',
                            defaultMessage: 'Expiration Date',
                          },
                        }}>
                        <DatePicker {...field} disablePast />
                      </FieldWrapperVertical>
                    )}
                  />
                </Grid2>
              </Grid2>
            )}
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center' }}>
            <Button type="submit" disabled={loading} variant="contained">
              Generate
            </Button>
            <Button variant="contained" color="secondary" onClick={handleClose}>
              Cancel
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
}
